import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {  useDevices, usePlant, useTcpClients, useUserPermissions } from "../../services/hooks";
import { useDeviceContext } from "../../context/socketContext";
import { Button, DetailCard, Dropdown, tCell } from "../utils";
import { buttonClasses } from "../utils/theme";
import "./DeviceMap.css";
import { GenericTableInfinite } from "../utils/GenericTable";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
export default function DeviceMap({}) {
	/**
	 * Plants have checkpoints
	 * Checkpoints have devices
	 * This map is to show all locations of the devices with their status (online/offline)
	 * Current active devices will come from TcpClients
	 * Socket update of device going offline/online will come from deviceData in useDeviceContext
	 *
	 * We'll first get all checkpoints from useCheckpoints - this will get us all checkpoints that user has access to
	 * From each checkpoint we will get the latitude and longitude and device id
	 * Using the lat and long we will plot the geopoint on the map
	 */
	const navigate = useNavigate();
	const { userPermissions, isFetched } = useUserPermissions();
	const [selectedPlantIds, setSelectedPlantIds] = useState([]);
	const mapRef = useRef(null);
	const [isMapLoaded, setIsMapLoaded] = useState(true);
	const [mapInstance, setMapInstance] = useState(null);
	const [showTable, setShowTable] = useState(false);
	const [filteredDeviceData, setFilteredDeviceData] = useState([]);
	const [filter, setFilter] = useState({});
	const { tcpClients, getTcpClients } = useTcpClients({pagesize: 1000});
	const { deviceData } = useDeviceContext();
	const { plantOptions, plants } = usePlant({}, "deviceMapPlants");
	const { devices, isLoading, getDevices } = useDevices({});
	const [isOpen, setIsOpen] = useState(false);
    const markerClusterRef = useRef(null); 
	
	let header = ["Device Name", "Status", "Warehouse", "IMEI", "Device Type"];
	let rows = [];

	if (showTable) {
		rows = filteredDeviceData.map((d) => {
			let d_info = deviceData[d.imei] !== undefined ? deviceData[d.imei] : tcpClients[d.imei];
			return [
				tCell(d?.Displayname || d?.displayName || "-"),
				tCell(<span className={`deviceStatus ${d_info ? "connected" : ""}`}></span>),
				tCell(d?.plantName || "-"),
				tCell(d?.imei || "-"),
				tCell(d?.deviceType || "-"),
			];
		});
	} else {
		rows = devices.map((d) => {
			let d_info = deviceData[d.imei] !== undefined ? deviceData[d.imei] : tcpClients[d.imei];
			return [
				tCell(d?.Displayname || d?.displayName || "-"),
				tCell(<span className={`deviceStatus ${d_info ? "connected" : ""}`}></span>),
				tCell(d?.imei || "-"),
			];
		});
	}

	const handleDetailClose = () => {
		setIsOpen(false);
		handleFilterClear();
		setShowTable(false);
	};

	const handleFilterChange = (name, value) => {
		setFilter((o) => ({ ...o, [name]: value }));
		setSelectedPlantIds(value);
		getDevices({})
		getTcpClients({pagesize: 1000})
	};

	const handleFilterClear =() => {
		setFilter({});
		setSelectedPlantIds([]);
		setFilteredDeviceData([]); 
		setShowTable(false);
		getDevices({})
		getTcpClients({pagesize: 1000})
	};

	const handleFilterSubmit = async () => {
		try {
			let res = null;
			if (filter.plantIds?.length) {
				res = await getDevices({ plantIds: filter.plantIds });
			} else {
				// if no plantIds, fetching all by passing []
				res = await getDevices({ plantIds: [] });
            }
			setFilteredDeviceData(res?.data?.data || []);
			setShowTable(true);
			initMap();
		} catch (err) {
			console.error("Error fetching devices for filter:", err);
			setFilteredDeviceData([]);
			setShowTable(true);
		}
	};

    const getCentroid = (coords) => {
        if (!coords || coords.length === 0) return null;
        const total = coords.length;
        const sum = coords.reduce((acc, point) => {
            acc.lat += point.lat;
            acc.lng += point.lng;
            return acc;
        }, { lat: 0, lng: 0 });
        return {
            lat: sum.lat / total,
            lng: sum.lng / total
        };
    };

    const getPlantDevices = async (plantId) => {
		try {
			const res = await getDevices({ plantIds: [plantId] });
			setFilteredDeviceData(res?.data?.data || []);
			setShowTable(true);
			setIsOpen(true);
		} catch (err) {
			console.error("Error fetching plant devices:", err);
			setFilteredDeviceData([]);
			setShowTable(true);
		}
    };
	async function initMap() {
		try {
			const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");

			const map = new google.maps.Map(mapRef.current, {
				center: { lat: 20.5937, lng: 78.9629 },
				zoom: 4.5,
				mapId: "deviceMap",
			});

			setMapInstance(map);

			// Clear existing cluster if present
			if (markerClusterRef.current) {
				markerClusterRef.current.clearMarkers();
			}

			const bounds = new google.maps.LatLngBounds();
			let markers = [];

			// Filter plants based on selection or show all
			const visiblePlants = selectedPlantIds.length > 0
				? plants.filter((plant) => selectedPlantIds.includes(plant._id))
				: plants;

			visiblePlants.forEach((plant) => {
				const coords = plant.factoryCoordinates;
				if (!coords || coords.length === 0) return;

				const centroid = getCentroid(coords);
				if (!centroid) return;

				bounds.extend(new google.maps.LatLng(centroid.lat, centroid.lng));
				
				// const plantCheckpoints = checkpoints.filter((cp) => cp.plantId === plant._id);
				const plantDevices = devices.filter((d) => d.plantId === plant._id);
				// console.log("plantDevice", plantDevices)
				const total = plantDevices.length;
				let onlineCount = 0;
				plantDevices.forEach((d) => {
					
					const isOnline =  deviceData[d.imei] !== undefined ? deviceData[d.imei] : tcpClients[d.imei]; 
					
						if (isOnline) {
							// console.log("ISONLINE", isOnline)
							onlineCount++;
						}
					});
					// console.log("onlineCount", onlineCount)
					// console.log("onlinecount", onlineCount, total)
				let backgroundColor;
				if (total > 0) {
					if (onlineCount === total) {
						backgroundColor = "#28a745"; // green
					} else if (onlineCount === 0) {
						backgroundColor = "red"; // red
					} else {
						backgroundColor = "#ff7700"; // orange
					}
				}else{
					backgroundColor = "red"; 
				}

				// Draw the polygon for the plant area
				const polygon = new google.maps.Polygon({
					paths: coords.map(c => ({ lat: c.lat, lng: c.lng })),
					strokeColor: "#a5624eff",
					strokeOpacity: 0.8,
					strokeWeight: 2,
					fillColor: "#a5624eff",
					fillOpacity: 0.35,
				});
				polygon.setMap(map);

					const pin = new PinElement({
						scale: 1,
						background: backgroundColor,
						borderColor: backgroundColor,
						glyphColor: "white",
					});

					const marker = new AdvancedMarkerElement({
						map: map,
						position: { lat: centroid.lat, lng: centroid.lng },
						title: plant.plantName,
						content: pin.element,
					});

				// Custom status for coloring in cluster
				marker.customStatus = total > 0 && onlineCount === total ? "online"
					: onlineCount === 0 ? "offline"
					: "partial";

				marker.addListener("click", () => {
					if (map) {
						map.panTo({ lat: centroid.lat, lng: centroid.lng });
						map.setZoom(16);
					}
					getPlantDevices(plant._id);
					// Auto-fill warehouse dropdown when marker clicked
					setFilter((o) => ({ ...o, plantIds: [plant._id] }));
					setSelectedPlantIds([plant._id]);
				});

					markers.push(marker);
			});

			if (!bounds.isEmpty()) {
				map.fitBounds(bounds);
				google.maps.event.addListenerOnce(map, "bounds_changed", () => {
					if (map.getZoom() > 16) {
						map.setZoom(16);
					}
				});
			}

			markerClusterRef.current = new MarkerClusterer({
				map,
				markers,
				renderer: {
					render: ({ count, markers, position }) => {
					  	const hasOnline = markers.some(m => m.customStatus === "online" || m.customStatus === "partial");
    					const hasOffline = markers.some(m => m.customStatus === "offline" || m.customStatus === "partial");
    					const backgroundColor = hasOnline && !hasOffline ? "#28a745" :!hasOnline && hasOffline ? "red" :"#ff7700";
						return new google.maps.Marker({
							position,
							icon: {
								path: google.maps.SymbolPath.CIRCLE,
								fillColor: backgroundColor,
								fillOpacity: 0.8,
								strokeColor: backgroundColor,
								strokeWeight: 1,
								scale: 15,
							},
							label: {
								text: String(count),
								color: "white",
								fontWeight: "bold",
							},
						});
					}
				}
			});

		} catch (error) {
			console.error("Error initializing map:", error);
			setIsMapLoaded(false);
		}
	}


	useEffect(() => {
		if (window.google && mapRef.current && plants.length > 0 && devices.length>0) {
			initMap();
		}
	}, [plants, devices, selectedPlantIds, tcpClients]);

	useEffect(() => {
		if (selectedPlantIds.length > 0) {
			(async () => {
				try {
					const res = await getDevices({ plantIds: selectedPlantIds });
					setFilteredDeviceData(res?.data?.data || []);
					setShowTable(true);
					setIsOpen(true);
				} catch (err) {
					console.error("Error fetching plant devices:", err);
					setFilteredDeviceData([]);
					setShowTable(true);
				}
			})();
		}
	}, [selectedPlantIds]);

	// IF SOCKET EVENT DOES NOT WORKS PROPERLY.... THEN IN THIS CASE CALLING GETDEVICES AFTER EVERY 90 SECS
	useEffect(() => {
		const fetchTcpClients = async () => {
			await getTcpClients({pagesize: 1000});
		};
		fetchTcpClients();
		// repeat every 90 seconds (90000 ms)
		const interval = setInterval(fetchTcpClients, 90000);
		return () => clearInterval(interval);
	}, []);

	if (isFetched && userPermissions && Object.keys(userPermissions).length && !userPermissions.viewDeviceMap?.value) return <Navigate to="/not-authorized" replace />;

	return (
		<div className="contentpanel">
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">Device Map</h1>
				</div>
			</div>

			<hr className="bgg" />

			<div className="main_content">
				<div style={{ position: "relative" }}>
					<Button
						text="Recenter"
						onClick={() => {
							if (mapInstance) {
								mapInstance.setCenter({ lat: 20.5937, lng: 78.9629 });
								mapInstance.setZoom(4.4);
							}
							// Clearing auto-filled dropdown
							setFilter({});
							setSelectedPlantIds([]);
							getDevices({})
							getTcpClients({pagesize: 1000})
							setIsOpen(false)
							setShowTable(false)
						}}
						style={{
							...buttonClasses.lynkitBlackFill,
							position: "absolute",
							top: "1rem",
							right: "9.5rem",
							zIndex: "1",
							width: "max-content",
						}}
					/>
					<Button
						text="Filters"
						onClick={() => {
							setIsOpen(true);
						}}
						style={{
							...buttonClasses.lynkitOrangeFill,
							position: "absolute",
							top: "1rem",
							right: "5rem",
							zIndex: "1",
							width: "max-content",
						}}
					/>
					<div ref={mapRef} style={{ width: "100%", height: "75vh" }} />
				</div>
				<DetailCard isOpen={isOpen} onClose={handleDetailClose} title={"Apply Filter"} height={"69vh"} width={"30vw"}>
					<Dropdown
						name="plantIds"
						label="Warehouse"
						options={plantOptions}
						value={plantOptions.filter((_) => filter.plantIds?.includes(_.value))}
						multiple
						onChange={(opt, name) => {
							handleFilterChange(
								name,
								opt?.map((_) => _.value)
							);
						}}
					/>
					<div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
						<Button text="Reset" onClick={handleFilterClear} style={buttonClasses.lynkitBlackFill} />
						<Button text="Apply Filter" onClick={handleFilterSubmit} style={buttonClasses.lynkitOrangeFill} />
					</div>
					{showTable ? (
						<div style={{ marginTop: "1rem", maxHeight: "52vh", minHeight: "22vh" }}>
							<GenericTableInfinite header={header} rows={rows} isLoading={isLoading} />
						</div>
					) : null}
				</DetailCard>
			</div>
			<div className="status-indicator-wrap">
				<div className="status-indicator">
					<span className="indicator online"></span> All Online
				</div>
				<div className="status-indicator">
					<span className="indicator offline"></span> All Offline
				</div>
				<div className="status-indicator">
					<span className="indicator fewOffline"></span> Few Offline
				</div>
			</div>
		</div>
	);
}
